'use client';

import React, { useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { useT } from '@/components/i18n-provider';
import { createPortal } from 'react-dom';
import { OptimizedImage } from '@/components/optimized-image';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX,
  Play,
  Pause,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AudioPlayer, getActiveAudioTime, pauseAllAudio, dispatchAudioSeek } from './post-media';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MediaLightboxType =
  | 'image'
  | 'gif'
  | 'video'
  | 'audio'
  | 'model3d'
  | '3d'
  | 'document'
  | 'pdf'
  | 'file'
  | 'unknown'
  | null;

export interface MediaLightboxItem {
  url: string;
  type: MediaLightboxType;
  contentId?: string;
  mimeType?: string;
  filename?: string;
  thumbnailUrl?: string | null;
}

export interface MediaLightboxCtx {
  item: MediaLightboxItem;
  index: number;
  total: number;
  close: () => void;
  metadataVisible: boolean;
}

type SlotValue = ReactNode | ((ctx: MediaLightboxCtx) => ReactNode);

export interface MediaLightboxProps {
  items: MediaLightboxItem[];
  initialIndex?: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
  /** Left side of top chrome (e.g. author / filename) */
  headerLeft?: SlotValue;
  /** Right-side action buttons in top chrome (rendered before the always-on close button) */
  headerActions?: SlotValue;
  /** Overlay anchored to the bottom of the media area (e.g. post text). Fades with chrome. */
  overlaySlot?: SlotValue;
  /** Bottom-bar slot rendered after VideoControlBar (e.g. engagement row). Fades with chrome. */
  footerSlot?: SlotValue;
  /** Render a custom body for the given item; return null/undefined to fall back to defaults. */
  renderBody?: (item: MediaLightboxItem) => ReactNode | null | undefined;
  /** Show 1/N counter when multi-item. Default: true. */
  showCounter?: boolean;
}

function resolveSlot(slot: SlotValue | undefined, ctx: MediaLightboxCtx): ReactNode {
  if (slot == null) return null;
  return typeof slot === 'function' ? slot(ctx) : slot;
}

// ---------------------------------------------------------------------------
// Inline media renderers
// ---------------------------------------------------------------------------

const LightboxVideo = React.forwardRef<HTMLVideoElement, { src: string; poster?: string }>(
  function LightboxVideo({ src, poster }, ref) {
    return (
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        autoPlay
        className="max-w-full max-h-full object-contain"
      />
    );
  },
);

function useVideoControls() {
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const videoRefCallback = useCallback((el: HTMLVideoElement | null) => {
    setVideoEl(el);
  }, []);

  useEffect(() => {
    if (!videoEl) return;
    videoEl.play().catch(() => setPaused(true));
    const onTime = () => setProgress(videoEl.currentTime);
    const onDur = () => setDuration(videoEl.duration || 0);
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    videoEl.addEventListener('timeupdate', onTime);
    videoEl.addEventListener('durationchange', onDur);
    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);
    return () => {
      videoEl.removeEventListener('timeupdate', onTime);
      videoEl.removeEventListener('durationchange', onDur);
      videoEl.removeEventListener('play', onPlay);
      videoEl.removeEventListener('pause', onPause);
    };
  }, [videoEl]);

  const togglePlay = useCallback(() => {
    if (!videoEl) return;
    if (videoEl.paused) videoEl.play().catch(() => {});
    else videoEl.pause();
  }, [videoEl]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (videoEl) videoEl.muted = next;
      return next;
    });
  }, [videoEl]);

  const seekTo = useCallback(
    (ratio: number) => {
      if (videoEl && duration) videoEl.currentTime = ratio * duration;
    },
    [videoEl, duration],
  );

  return { videoRefCallback, muted, paused, progress, duration, togglePlay, toggleMute, seekTo };
}

function VideoControlBar({
  paused,
  muted,
  progress,
  duration,
  onTogglePlay,
  onToggleMute,
  onSeek,
}: {
  paused: boolean;
  muted: boolean;
  progress: number;
  duration: number;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onSeek: (ratio: number) => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  const handleSeek = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      const bar = barRef.current;
      if (!bar || !duration) return;
      const rect = bar.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      onSeek(ratio);
    },
    [duration, onSeek],
  );

  return (
    <div className="px-4 pt-1 pb-1">
      <div
        ref={barRef}
        className="h-5 flex items-center cursor-pointer"
        onClick={handleSeek}
        onTouchStart={handleSeek}
      >
        <div className="w-full h-1 bg-white/20 rounded-full relative group/bar hover:h-1.5 transition-all">
          <div
            className="absolute inset-y-0 left-0 bg-white rounded-full transition-[width] duration-75"
            style={{ width: `${pct}%` }}
          />
          <div
            className="absolute top-1/2 h-3 w-3 bg-white rounded-full opacity-0 group-hover/bar:opacity-100 transition-opacity"
            style={{ left: `${pct}%`, transform: `translate(-50%, -50%)` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between mt-0.5">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePlay();
            }}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors text-white"
          >
            {paused ? <Play className="h-4 w-4 fill-white ml-0.5" /> : <Pause className="h-4 w-4 fill-white" />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleMute();
            }}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors text-white"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
        <span className="text-[11px] text-white/50 tabular-nums">
          {fmtTime(progress)} / {fmtTime(duration)}
        </span>
      </div>
    </div>
  );
}

function LightboxImage({ src, alt }: { src: string; alt?: string }) {
  return (
    <div className="relative w-full h-full">
      <OptimizedImage
        src={src}
        alt={alt || 'Image'}
        fill
        className="object-contain select-none"
        sizes="100vw"
        draggable={false}
      />
    </div>
  );
}

function Lightbox3D({ src, poster }: { src: string; poster?: string }) {
  const tr = useT();
  return (
    <div className="w-full h-full flex items-center justify-center">
      {/* @ts-expect-error model-viewer is a web component */}
      <model-viewer
        src={src}
        poster={poster}
        alt={tr('contentPreview.model3d')}
        auto-rotate
        camera-controls
        shadow-intensity="1"
        environment-image="neutral"
        exposure="1.2"
        tone-mapping="agx"
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MediaLightbox
// ---------------------------------------------------------------------------

export function MediaLightbox({
  items,
  initialIndex = 0,
  onClose,
  onIndexChange,
  headerLeft,
  headerActions,
  overlaySlot,
  footerSlot,
  renderBody,
  showCounter = true,
}: MediaLightboxProps) {
  const tr = useT();
  const [index, setIndex] = useState(initialIndex);
  const [mounted, setMounted] = useState(false);
  const [metadataVisible, setMetadataVisible] = useState(true);

  const item = items[index];
  const hasMultiple = items.length > 1;

  const videoState = useVideoControls();

  // Capture any currently-playing card audio progress, then pause it (during render,
  // before child effects). When the lightbox closes we sync that timestamp back so
  // the small inline player resumes at the same spot.
  const savedTimeRef = useRef(0);
  const didPauseRef = useRef(false);
  if (!didPauseRef.current) {
    savedTimeRef.current = getActiveAudioTime();
    pauseAllAudio();
    didPauseRef.current = true;
  }

  const handleClose = useCallback(() => {
    const current = items[index];
    if (current?.url) {
      const time = getActiveAudioTime();
      pauseAllAudio();
      dispatchAudioSeek(current.url, time);
    }
    onClose();
  }, [items, index, onClose]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock scroll while open
  useEffect(() => {
    const mainScroll = document.getElementById('main-scroll');
    const prevBody = document.body.style.overflow;
    const prevMain = mainScroll?.style.overflow;
    document.body.style.overflow = 'hidden';
    if (mainScroll) mainScroll.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      if (mainScroll) mainScroll.style.overflow = prevMain || '';
    };
  }, []);

  // Native horizontal scroll-snap drives the carousel: swiping reveals the
  // neighbouring slide and the browser snaps to the nearest one on release — so a
  // short drag bounces back (no switch) and a long one advances. The scroll
  // position is the single source of truth for the active index.
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollAt = useRef(0);
  const animRef = useRef<number | null>(null);

  // Custom eased snap — the browser's `scrollTo({behavior:'smooth'})` lands
  // abruptly. easeOutBack gives a soft elastic settle (a gentle overshoot well
  // under half a slide, so the index never flips early). Snapping is turned off
  // for the duration so CSS mandatory-snap can't fight the animation, then
  // restored when it finishes.
  const animateScrollTo = useCallback((target: number, duration = 420) => {
    const el = scrollRef.current;
    if (!el) return;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    el.style.scrollSnapType = 'none';
    const start = el.scrollLeft;
    const dist = target - start;
    if (Math.abs(dist) < 1) {
      el.style.scrollSnapType = '';
      return;
    }
    const t0 = performance.now();
    const ease = (t: number) => {
      const c1 = 1.2;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      el.scrollLeft = start + dist * ease(t);
      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
        el.style.scrollSnapType = '';
      }
    };
    animRef.current = requestAnimationFrame(step);
  }, []);

  const scrollToIndex = useCallback(
    (i: number, smooth = true) => {
      const el = scrollRef.current;
      if (!el) return;
      const target = Math.max(0, Math.min(items.length - 1, i)) * el.clientWidth;
      if (smooth) animateScrollTo(target);
      else el.scrollLeft = target;
    },
    [items.length, animateScrollTo],
  );

  const goNext = useCallback(() => scrollToIndex(index + 1), [index, scrollToIndex]);
  const goPrev = useCallback(() => scrollToIndex(index - 1), [index, scrollToIndex]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.clientWidth) return;
    lastScrollAt.current = Date.now();
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index && i >= 0 && i < items.length) {
      setIndex(i);
      onIndexChange?.(i);
    }
  }, [index, items.length, onIndexChange]);

  // Touch + two-finger trackpad swipe scroll the snap container natively. A
  // MOUSE / trackpad press-drag does NOT, so we drag-to-scroll: temporarily
  // disable snapping, move scrollLeft with the pointer (the neighbour follows),
  // then snap to the nearest slide on release.
  const dragRef = useRef<{ startX: number; startScroll: number; pointerId: number; moved: boolean } | null>(null);

  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      const el = scrollRef.current;
      if (!el || !hasMultiple || e.pointerType === 'touch') return;
      if ((e.target as HTMLElement).closest('a, button, input, model-viewer, video')) return;
      dragRef.current = { startX: e.clientX, startScroll: el.scrollLeft, pointerId: e.pointerId, moved: false };
      el.style.scrollSnapType = 'none';
    },
    [hasMultiple],
  );

  const onDragMove = useCallback((e: React.PointerEvent) => {
    const s = dragRef.current;
    const el = scrollRef.current;
    if (!s || !el) return;
    const dx = e.clientX - s.startX;
    if (!s.moved) {
      if (Math.abs(dx) <= 4) return;
      s.moved = true;
      try {
        el.setPointerCapture(s.pointerId);
      } catch {
        /* ignore */
      }
    }
    el.scrollLeft = s.startScroll - dx;
  }, []);

  const onDragEnd = useCallback(() => {
    const s = dragRef.current;
    const el = scrollRef.current;
    dragRef.current = null;
    if (!el) return;
    if (s && s.moved) {
      lastScrollAt.current = Date.now();
      // Eased snap to the nearest slide (animateScrollTo restores snapping when done).
      scrollToIndex(Math.round(el.scrollLeft / el.clientWidth));
    } else {
      el.style.scrollSnapType = ''; // pure click → restore (drag-start disabled it)
    }
  }, [scrollToIndex]);

  // Keyboard. Capture-phase + preventDefault on Escape so layers UNDER the
  // lightbox (the Discover level stack, the canvas Stage exit — both respect
  // defaultPrevented) never treat the same press as theirs: the topmost
  // surface consumes Escape. Arrows skip fields so a slot input stays typable.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [handleClose, goNext, goPrev]);

  // Place the scroll position on the requested slide once the snap track is in
  // the DOM (the portal mounts after the first render flips `mounted`).
  useEffect(() => {
    const el = scrollRef.current;
    if (mounted && el && el.clientWidth) el.scrollLeft = initialIndex * el.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // Cancel any in-flight snap animation on unmount.
  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  if (!item) return null;

  const ctx: MediaLightboxCtx = {
    item,
    index,
    total: items.length,
    close: handleClose,
    metadataVisible,
  };

  const isVideo = item.type === 'video';

  // Render one slide. The active slide gets the full player; neighbours render a
  // lightweight still (poster/image) so a swipe reveals them without spinning up
  // extra video/audio/3D contexts.
  const renderSlide = (it: MediaLightboxItem, i: number) => {
    const custom = renderBody?.(it);
    if (custom != null) return custom;
    const t = it.type;
    const active = i === index;
    if (t === 'video') {
      if (active) {
        return (
          <div
            className="w-full h-full flex items-center justify-center cursor-pointer"
            onClick={() => videoState.togglePlay()}
          >
            <LightboxVideo ref={videoState.videoRefCallback} src={it.url} poster={it.thumbnailUrl || undefined} />
            {videoState.paused && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="h-16 w-16 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
                  <Play className="h-8 w-8 text-white fill-white ml-1" />
                </div>
              </div>
            )}
          </div>
        );
      }
      return it.thumbnailUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={it.thumbnailUrl} alt="" className="max-w-full max-h-full object-contain" />
      ) : (
        <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center">
          <Play className="h-8 w-8 text-white/70 fill-white/70 ml-1" />
        </div>
      );
    }
    if (t === 'image' || t === 'gif') return <LightboxImage src={it.url} alt={it.filename} />;
    if (t === 'model3d' || t === '3d') {
      return active ? (
        <Lightbox3D src={it.url} poster={it.thumbnailUrl || undefined} />
      ) : it.thumbnailUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={it.thumbnailUrl} alt="" className="max-w-full max-h-full object-contain" />
      ) : (
        <div className="text-white/40 text-sm">{tr('contentPreview.model3d')}</div>
      );
    }
    if (t === 'audio') {
      return active ? (
        <div className="w-full max-w-2xl mx-auto px-4">
          <AudioPlayer src={it.url} thumbnail={it.thumbnailUrl} dark autoPlay startTime={savedTimeRef.current} />
        </div>
      ) : it.thumbnailUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={it.thumbnailUrl} alt="" className="max-h-full max-w-full object-contain" />
      ) : (
        <div className="text-white/40 text-sm">Audio</div>
      );
    }
    return null;
  };

  const lightboxContent = (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col overflow-hidden">
      {/* MEDIA AREA */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {/* TOP CHROME */}
        <div
          className={cn(
            'absolute top-0 left-0 right-0 z-20 pointer-events-none transition-opacity duration-300',
            metadataVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          <div className="bg-gradient-to-b from-black/80 via-black/40 to-transparent pb-8 pointer-events-auto">
            <div className="flex items-center justify-between px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-1">
              <div className="flex items-center gap-3 min-w-0">{resolveSlot(headerLeft, ctx)}</div>
              <div className="flex items-center gap-1.5 shrink-0">
                {hasMultiple && showCounter && (
                  <span className="text-xs text-white/40 tabular-nums mr-1">
                    {index + 1}/{items.length}
                  </span>
                )}
                {resolveSlot(headerActions, ctx)}
                <button
                  onClick={handleClose}
                  className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 text-white/60 transition-colors"
                  aria-label={tr('workspace.canvas.node.close')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SCROLL-SNAP TRACK — the browser reveals the neighbour as you swipe and
            snaps to the nearest slide on release (short drag bounces back, long
            drag advances). overscroll-x-contain stops the trackpad history gesture. */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          className={cn(
            'absolute inset-0 flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain no-scrollbar',
            hasMultiple && 'cursor-grab active:cursor-grabbing',
          )}
          onClick={(e) => {
            if (Date.now() - lastScrollAt.current < 220) return; // just swiped
            const target = e.target as HTMLElement;
            if (target.closest('a, button, input, model-viewer, video, audio')) return;
            const el = scrollRef.current;
            if (el && hasMultiple) {
              const rect = el.getBoundingClientRect();
              const x = e.clientX - rect.left;
              if (x < rect.width * 0.18) return goPrev();
              if (x > rect.width * 0.82) return goNext();
            }
            setMetadataVisible((v) => !v);
          }}
        >
          {items.map((it, i) => (
            <div
              key={i}
              className="relative h-full w-full shrink-0 snap-center snap-always flex items-center justify-center"
            >
              {renderSlide(it, i)}
            </div>
          ))}
        </div>

        {/* OVERLAY (e.g. post text) */}
        {overlaySlot && (
          <div
            className={cn(
              'absolute bottom-0 left-0 right-0 z-10 pointer-events-none max-h-44 flex flex-col transition-opacity duration-300',
              metadataVisible ? 'opacity-100' : 'opacity-0',
            )}
          >
            {resolveSlot(overlaySlot, ctx)}
          </div>
        )}

        {/* DESKTOP NAV ARROWS (edge-click is handled by the track's onClick by
            position, so the swipe gesture is never blocked by an overlay button) */}
        {hasMultiple && index > 0 && (
          <button
            onClick={goPrev}
            className="absolute left-3 top-1/2 z-[2] -translate-y-1/2 h-9 w-9 rounded-full bg-black/40 hover:bg-black/60 items-center justify-center text-white/80 transition-colors hidden md:flex backdrop-blur-sm"
            aria-label={tr('lightbox.previous')}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {hasMultiple && index < items.length - 1 && (
          <button
            onClick={goNext}
            className="absolute right-3 top-1/2 z-[2] -translate-y-1/2 h-9 w-9 rounded-full bg-black/40 hover:bg-black/60 items-center justify-center text-white/80 transition-colors hidden md:flex backdrop-blur-sm"
            aria-label={tr('lightbox.next')}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        {/* MOBILE DOT COLUMN */}
        {hasMultiple && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 md:hidden z-20">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-all',
                  i === index ? 'bg-white scale-125' : 'bg-white/30',
                )}
                aria-label={`Item ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* BOTTOM BAR */}
      {(isVideo || footerSlot) && (
        <div
          className={cn(
            'shrink-0 bg-black border-t border-white/[0.06] pb-[env(safe-area-inset-bottom)] transition-opacity duration-300',
            metadataVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        >
          {isVideo && (
            <VideoControlBar
              paused={videoState.paused}
              muted={videoState.muted}
              progress={videoState.progress}
              duration={videoState.duration}
              onTogglePlay={videoState.togglePlay}
              onToggleMute={videoState.toggleMute}
              onSeek={videoState.seekTo}
            />
          )}
          {resolveSlot(footerSlot, ctx)}
        </div>
      )}
    </div>
  );

  if (!mounted) return null;
  return createPortal(lightboxContent, document.body);
}
